<template>
  <div data-testid="rx-configurator" class="flex flex-col bg-shade-200">
    <!-- simplified header bar -->
    <RxHeader class="hidden md:block" @click:logo="close">
      <FimButton
        data-testid="rx-header-close"
        is-small
        type="tertiary"
        class="text-sm font-normal md:hidden"
        @click="close"
      >
        {{ $t('rxConfigurator.close') }}
      </FimButton>
    </RxHeader>

    <!-- content -->
    <div
      class="flex min-h-dvh flex-1 flex-col md:min-h-[calc(100vh-65px)] md:flex-row"
    >
      <!-- Product image -->
      <div
        class="mobile-scroll-animation-shrink sticky top-0 flex h-[50vh] transition-height md:top-[65px] md:h-[calc(100vh-65px)] md:w-1/2 xl:w-2/3"
        :class="{
          'md:hidden': isStep3LensQuality,
          'pb-8': !isVtoOpen,
        }"
      >
        <div class="size-full bg-shade-200">
          <div class="relative size-full">
            <HorizontalItemsScroller
              v-show="!showVTO"
              class="size-full"
              loop
              dots
              :has-gradient="false"
              :wrapper-classes="{
                'mix-blend-multiply': true,
              }"
            >
              <ScrollerItem
                v-for="(image, index) in productImages"
                :key="'slide-' + index"
                :data-index="index"
                class="item relative flex aspect-[1920/1075] size-full shrink-0 grow-0 basis-full snap-start items-stretch justify-center p-14 md:px-24 xl:px-36"
              >
                <NuxtPicture
                  :alt="productName"
                  quality="85"
                  :width="1920"
                  :height="1075"
                  :src="image.hash"
                  format="avif,webp"
                  placeholder="[50, 25, 50, 5]"
                  :preload="index === 0"
                  :loading="index === 0 ? 'eager' : 'lazy'"
                  sizes="sm:200vw md:200vw lg:200vw lg:200vw 2lg:200vw 3lg:200vw 4lg:200vw"
                  provider="fim"
                  class="picture picture-contain block aspect-[1920/1075] size-full mix-blend-multiply"
                />
              </ScrollerItem>
            </HorizontalItemsScroller>

            <div
              v-if="isStep4Extras"
              class="absolute inset-0 flex items-center justify-center"
            >
              <RxVTOFittingBox
                :is-open="isVtoOpen"
                :lens-color="currentTintingColor"
                class="size-full"
                :setup-initialization="setupInitialization"
                :fit-mix-ready="fitMixReady"
                :is-screenshot-in-progress="isScreenshotInProgress"
                @on-vto-close="handleVTOClose"
                @on-vto-open="handleVTOOpen"
                @on-screenshot="handleScreenshot"
              />
            </div>

            <div class="absolute bottom-4 w-full">
              <div class="mt-4 hidden items-center md:flex">
                <BadgeLegacy
                  class="mx-auto"
                  size="large"
                  type="info"
                  :label="getProductName(frameProduct, '')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Config steps -->
      <section
        class="z-10 flex w-full flex-1 flex-col rounded-t-xl bg-white md:w-1/2 md:rounded-none xl:w-1/3"
      >
        <RxConfiguratorLoading
          v-if="isLoading"
          :is-editing="editingGroupId !== undefined"
        />
        <RxConfiguratorError
          v-else-if="isError"
          :frame-product="frameProduct"
        />
        <template v-else>
          <TopNavigation
            :show-back="currentStep.id > 1"
            class="sticky top-0 z-[100] rounded-t-xl border-b border-shade-300 bg-white md:top-[65px] md:rounded-none"
            @click:back="goToPrevStep"
            @click:close="close"
          >
            {{ currentStep.title }}
          </TopNavigation>
          <main class="flex-1">
            <component
              :is="currentStep.component"
              rel="contentRef"
              v-bind="currentStep.props"
              @ui:update="updateUi"
              @step:go-to="goToStep"
              @step:select-option="selectStepOption"
            />
          </main>
          <CustomerServiceBox
            additional-text-key="customer_service_box.description_rx"
          />

          <footer class="sticky bottom-0 z-[90] bg-white @container">
            <SimpleProcessBar
              :current="currentStepIndex"
              :length="steps.length"
            />
            <div
              v-if="isStep5Summary"
              v-dompurify-html="$t('rxConfigurator.summary.below_CTA')"
              class="my-4 text-center text-base"
            ></div>
            <SumBar
              v-else
              :title="$t('rxConfigurator.sumbar.title')"
              :total-price="sumBarTotalPrice"
              :details="sumBarDetails"
              class="p-2"
            />
            <div class="p-4 pt-0 md:flex md:justify-center">
              <RxConfiguratorMainActionButton
                :main-action="mainAction"
                :disabled="mainActionButtonDisabled"
                :editing-group-id="editingGroupId"
                class="@4xl:max-w-5xl"
                :label="mainActionButtonLabel"
                @add-to-basket="addToBasket"
                @step:next="goToNextStep"
              />
            </div>
            <div
              v-if="currentStep.name === RxStepNames.PRESCRIPTION"
              class="p-4 pt-0 md:flex md:justify-center"
            >
              <RxConfiguratorMainActionButton
                main-action="skipValues"
                :editing-group-id="editingGroupId"
                class="@4xl:max-w-5xl"
                @click="handleSkipPrescriptionValues"
              />
            </div>
          </footer>
        </template>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint sonarjs/no-duplicate-string: 1 */

import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useEventListener } from '@vueuse/core'
import {
  Step1LensType,
  Step2Prescription,
  Step2PrescriptionValues,
  Step2PrescriptionConfirm,
  Step3LensQuality,
  Step4Extras,
  Step5Summary,
} from '#components'
import type { IRxConfiguratorStep } from './RxConfigurator' // explicit

const props = defineProps({
  frameProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
  frameVariant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  initialStep: {
    type: Number as PropType<number>,
    default: undefined,
  },
  editingGroupId: {
    type: String as PropType<string>,
    default: undefined,
  },
  hasPrescriptionValues: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const emit = defineEmits([
  'addToBasket',
  'step:changed',
  'click:close',
  'click:cancelPrescriptionEditor',
  'onVtoClose',
  'onVtoOpen',
  'addFrameToBasket',
])

const $i18n = useI18n()
const scope = effectScope()
onScopeDispose(() => scope.stop())

/** product info */

const productName = getProductName(props.frameProduct)
const productImages = computed(() =>
  getProductImagesForGallery(props.frameProduct, props.frameVariant),
)
const { getCheapestLensForFrame } = await useProductPrice()

/** tracking */

const { trackRxConfigurator } = await useTrackingEvents()

const track = () => {
  trackRxConfigurator({
    productDetails: props.frameProduct,
    configurator: {
      id: 'RX Configurator',
      step: currentStep.value.id,
      stepName: currentStep.value.name,
    },
    configuration: rxConfiguratorState.value,
  })
}

/** rx state */

const {
  init,
  reset,
  state,
  isPrescriptionValid,
  supportsPrescriptionValues,
  serialize,
  state: rxConfiguratorState,
  isError,
  setPrescriptionValues,
  resetPrescriptionValues,
  fetchFittingHeight,
  isHomeTryOn,
  setIsHomeTryOn,
} = await useRxConfiguratorState()

const mapTintingToVTOColor = (referenceKey?: string): string | undefined => {
  if (!referenceKey) return undefined

  // Clear or blue light filter
  if (
    referenceKey === 'cr_tinting_fbl-shc' ||
    referenceKey === 'cr_tinting_fbl-busc'
  )
    return 'NO_TINTING'

  if (referenceKey.includes('br')) return 'FIELMANN_Brown'
  if (referenceKey.includes('gr')) return 'FIELMANN_Grey'
  if (referenceKey.includes('g1')) return 'FIELMANN_Pioneer'

  return undefined
}

const currentTintingColor = computed(() =>
  mapTintingToVTOColor(state.value?.tintingType?.variant?.referenceKey),
)

const { data: prescriptionValuesFromOrder, isPrescriptionValuesFromOrderSet } =
  usePrescriptionValuesFromOrder()

const completed = ref(false)
const isLoading = ref(false)
const hasPrescriptionValues = ref(props.hasPrescriptionValues)

scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(isError, () => {
    if (isError.value) {
      isLoading.value = false
    }
  })
})

onBeforeMount(() => {
  if (state.value.frame?.variant.id !== props.frameVariant.id) {
    reset()
  }
})

let cleanup: ReturnType<typeof useEventListener>
onMounted(async () => {
  await init(props.frameProduct, props.frameVariant)

  if (
    !props.initialStep &&
    supportsPrescriptionValues.value &&
    isPrescriptionValuesFromOrderSet.value
  ) {
    setPrescriptionValues(
      formatPrescriptionToRxConfigurator(
        prescriptionValuesFromOrder.value.prescriptionValues!,
      ),
    )
    fetchFittingHeight()
  }

  pushVirtualHistoryStep({ rxConfiguratorStep: 0, virtualHistoryStep: 0 })
  cleanup = useEventListener('popstate', onBrowserNav)
})

onBeforeUnmount(() => {
  if (cleanup) {
    cleanup()
  }
})

const close = () => {
  emit('click:close')
}

const addToBasket = async () => {
  isLoading.value = true
  track()

  if (isHomeTryOn.value) {
    emit('addFrameToBasket')
    setIsHomeTryOn(false)
    return
  }

  emit('addToBasket', {
    itemsSerialized: await serialize(props.editingGroupId),
    rxConfiguration: rxConfiguratorState.value,
  })
}

/** steps */

const currentStepIndex = ref(props.initialStep ?? 0)
const currentStep = computed(() => steps.value[currentStepIndex.value])

const steps = computed(() => {
  const allSteps: IRxConfiguratorStep[] = [
    {
      name: RxStepNames.PRESCRIPTION_TYPE,
      title: $i18n.t('rxConfigurator.step.lensType'),
      id: 1,
      canProceedToNextStep: () => !!state.value.lensType,
      skippable: false,
      component: Step1LensType,
    },
    {
      name: RxStepNames.PRESCRIPTION,
      title: $i18n.t('rxConfigurator.step.prescriptionValues'),
      id: 2,
      canProceedToNextStep: () => isPrescriptionValid.value,
      skippable: !supportsPrescriptionValues.value || skipPrescription.value,
      component: Step2Prescription,
    },
    {
      name: RxStepNames.PRESCRIPTION_VALUES,
      title: $i18n.t('rxConfigurator.step.prescriptionValues'),
      id: 3,
      canProceedToNextStep: () => isPrescriptionValid.value,
      skippable: !supportsPrescriptionValues.value || skipPrescription.value,
      component: Step2PrescriptionValues,
    },
    {
      name: RxStepNames.PRESCRIPTION_CONFIRM,
      title: $i18n.t('rxConfigurator.step.prescriptionConfirm'),
      id: 4,
      canProceedToNextStep: () => isPrescriptionValid.value,
      skippable: !supportsPrescriptionValues.value || skipPrescription.value,
      component: Step2PrescriptionConfirm,
      props: {
        hasPrescriptionValues: hasPrescriptionValues.value,
      },
    },
    {
      name: RxStepNames.GLASS_TYPE,
      title: $i18n.t('rxConfigurator.step.lensQuality'),
      id: 5,
      canProceedToNextStep: () => !!state.value.lensQuality,
      skippable: false,
      component: Step3LensQuality,
    },
    {
      name: RxStepNames.EXTRAS,
      title: $i18n.t('rxConfigurator.step.extras'),
      id: 6,
      canProceedToNextStep: () => !!state.value.tintingType,
      skippable: false,
      component: Step4Extras,
      props: {
        handleVTOOpen,
        fitMixReady,
        isVtoOpen,
      },
    },
    {
      name: RxStepNames.SUMMARY,
      title: $i18n.t('rxConfigurator.step.summary'),
      id: 7,
      canProceedToNextStep: () => false,
      skippable: false,
      component: Step5Summary,
      props: {
        hasPrescriptionValues: hasPrescriptionValues.value,
      },
    },
  ]

  return allSteps.filter(({ skippable }) => !skippable)
})

const isStep1LensType = computed(
  () => currentStep.value.component === Step1LensType,
)
const isStep3LensQuality = computed(
  () => currentStep.value.component === Step3LensQuality,
)
const isStep5Summary = computed(
  () => currentStep.value.component === Step5Summary,
)

const skipPrescription = computed(
  () =>
    (isPrescriptionValuesFromOrderSet.value ||
      !!prescriptionValuesFromOrder.value.isConvertibleProgressiveGlasses) &&
    !completed.value &&
    !props.initialStep,
)

/** sum bar */

const getSumbarLensQuality = (
  lensQuality?: ConfiguratorStateValue,
  frame?: ConfiguratorStateValue,
): ConfiguratorStateValue | undefined => {
  if (!lensQuality) {
    if (!frame || isHomeTryOn.value) {
      return undefined
    }
    const cheapestLensForFrame = getCheapestLensForFrame(frame.product)
    return cheapestLensForFrame.product
      ? {
          product: cheapestLensForFrame.product,
          variant: cheapestLensForFrame.variant,
        }
      : undefined
  }
  return lensQuality
}

const sumBarItems = computed(() => {
  const lensQuality = getSumbarLensQuality(
    state.value.lensQuality,
    state.value.frame,
  )
  return { ...state.value, lensQuality }
})

const sumBarDetails = computed(() => {
  return [
    {
      title: $i18n.t('rxConfigurator.sumbar.frame'),
      price: sumBarItems.value.frame?.variant?.price,
      alternate: false,
    },
    // Only want to show this if price is > 0
    sumBarItems.value.lensType?.variant?.price?.withTax && {
      title: $i18n.t('rxConfigurator.sumbar.lensType'),
      price: sumBarItems.value.lensType?.variant?.price,
      alternate: false,
    },
    {
      title: sumBarItems.value.lensQuality
        ? getLensName(sumBarItems.value.lensQuality.product)
        : $i18n.t('rxConfigurator.sumbar.glasses'),
      price: sumBarItems.value.lensQuality?.variant?.price,
      alternate: !sumBarItems.value.lensQuality,
    },
    {
      title: $i18n.t('rxConfigurator.sumbar.extras'),
      price: sumBarItems.value.tintingType?.variant?.price,
      alternate: !sumBarItems.value.tintingType,
    },
    {
      title: sumBarItems.value.freeService
        ? getProductName(sumBarItems.value.freeService?.product, '')
        : '',
      price: sumBarItems.value.freeService?.variant?.price,
      alternate: false,
    },
  ].filter(Boolean)
})

const sumBarTotalPrice = computed(() => {
  const prices = [
    sumBarItems.value.frame?.variant?.price,
    sumBarItems.value.lensQuality?.variant?.price,
    sumBarItems.value.lensType?.variant?.price,
    sumBarItems.value.tintingType?.variant?.price,
  ].filter(Boolean)
  return prices.length ? sumUpPrice(prices) : undefined
})

/** main action button */

const mainAction = ref<RxMainAction>('continue')
const mainActionButtonDisabled = ref(false)
const mainActionButtonLabel = ref($i18n.t('rxConfigurator.continue'))

const updateUi = (params?: {
  mainAction?: RxMainAction
  buttonLabel?: string
  disabled?: boolean
}) => {
  if (!params) return

  if (params?.mainAction) {
    mainAction.value = params.mainAction
  }

  if (params?.mainAction === 'addToBasket') {
    mainActionButtonLabel.value = props.editingGroupId
      ? $i18n.t('rxConfigurator.summary.update_basket_item')
      : $i18n.t('rxConfigurator.summary.add_to_basket')
  } else {
    mainActionButtonLabel.value =
      params?.buttonLabel || $i18n.t('rxConfigurator.continue')
  }

  mainActionButtonDisabled.value = params?.disabled || false
}

/** navigating between steps */

const selectStepOption = (params?: { hasPrescription: boolean }) => {
  if (!params) return

  if ('hasPrescription' in params) {
    hasPrescriptionValues.value = Boolean(params?.hasPrescription)
  }
}

const goToNextStep = () => {
  track()
  currentStepIndex.value++

  if (
    steps.value?.[currentStepIndex.value]?.name ===
      RxStepNames.PRESCRIPTION_CONFIRM &&
    !skipPrescription.value
  ) {
    fetchFittingHeight()
  }

  if (
    steps.value.length - 1 < currentStepIndex.value + 1 &&
    skipPrescription.value
  ) {
    // We reset the skipPrescription flag so can go back to the prescription step
    completed.value = true

    // We go to the summary step
    goToStep(steps.value.length - 1)
  }

  if (isVtoOpen.value) {
    handleVTOClose()
  }
}

const goToPrevStep = () => {
  // if we're on the first step we close the configurator
  if (currentStepIndex.value < 1) {
    close()

    // special case: previously skipped prescription values
    // identified by current step + invalid prescription values
  } else if (
    !isPrescriptionValid.value &&
    steps.value?.[currentStepIndex.value]?.name === RxStepNames.GLASS_TYPE
  ) {
    currentStepIndex.value = steps.value.findIndex(
      (step) => step.name === RxStepNames.PRESCRIPTION,
    )

    // otherwise we just decrease the current step
  } else {
    currentStepIndex.value--
  }

  // the main action button should be enabled when going back
  // otherwise we wouldn't have gotten this far
  mainActionButtonDisabled.value = false

  if (isVtoOpen.value) {
    handleVTOClose()
  }
}

const goToStep = (i: number = 0) => {
  currentStepIndex.value = i
}

const handleSkipPrescriptionValues = () => {
  track()
  resetPrescriptionValues()
  goToStep(
    steps.value.findIndex((step) => step.name === RxStepNames.GLASS_TYPE),
  )

  hasPrescriptionValues.value = false
}

/** browser navigation */

const isBrowserNav = ref(false)
const onBrowserNav = (e: PopStateEvent) => {
  isBrowserNav.value = true
  if (Number.isNaN(e.state?.rxConfiguratorStep)) {
    close()
  } else {
    currentStepIndex.value = e.state?.rxConfiguratorStep || 0
  }
}

scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(currentStepIndex, (_next, _prev) => {
    emit('step:changed')
    if (isBrowserNav.value) {
      // browser navigation has been used, stepIndex has already been adjusted so we can reset the flag
      isBrowserNav.value = false
    } else {
      pushVirtualHistoryStep({
        rxConfiguratorStep: currentStepIndex.value || 0,
      })
    }
  })
})

/** VTO */

const isVtoOpen = ref(false)

const {
  setupInitialization,
  openVirtualWindow,
  closeVirtualWindow,
  fitMixReady,
  isScreenshotInProgress,
  handleScreenshot,
} = useRxVTO({
  product: props.frameProduct,
  variant: props.frameVariant,
  containerId: 'rx-fitmix-container',
  lensColor: currentTintingColor,
})

const isStep4Extras = computed(
  () => currentStep.value.component === Step4Extras,
)
const showVTO = computed(() => isStep4Extras.value && isVtoOpen.value)

const handleVTOClose = () => {
  isVtoOpen.value = false
  emit('onVtoClose')
  closeVirtualWindow()
}

const handleVTOOpen = (event: Event) => {
  isVtoOpen.value = true
  emit('onVtoOpen')
  openVirtualWindow(event)
}

definePageMeta({ pageType: 'rx_configurator' })
</script>
